import React, { useState, useEffect } from 'react';
import { Building2, Search, RotateCcw, Trash2, Archive } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import AdminLayout from '../../components/admin/AdminLayout';
import ConfirmModal from '../../components/ConfirmModal';
import { adminApi } from '../../lib/api';

import { toast } from 'sonner';
import { PHASE1_TIERS, LEGACY_TIER } from './BusinessManagement';

// Archived rows carry only a plan key, so name it through the same Phase 1 lookup the
// live business list uses: free / local / multi by name, anything else "Legacy".
const tierLabel = (tierKey) =>
  (tierKey ? PHASE1_TIERS[tierKey]?.label || LEGACY_TIER.label : PHASE1_TIERS.free.label);

const ArchivedBusinesses = () => {
  const [businesses, setBusinesses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [restoreModal, setRestoreModal] = useState({ isOpen: false, businessId: null, businessName: '' });
  const [deleteModal, setDeleteModal] = useState({ isOpen: false, businessId: null, businessName: '' });
  const [actionLoading, setActionLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    fetchArchivedBusinesses();
  }, []);

  const fetchArchivedBusinesses = async () => {
    try {
      setLoading(true);
      const response = await adminApi.get(`/admin/businesses/archived/list`);
      setBusinesses(response.data.businesses);
    } catch (err) {
      console.error('Error fetching archived businesses:', err);
      toast.error('Failed to load archived businesses');
    } finally {
      setLoading(false);
    }
  };

  const openRestoreModal = (business) => {
    setRestoreModal({ isOpen: true, businessId: business.id, businessName: business.name });
    setErrorMessage('');
  };

  const closeRestoreModal = () => {
    setRestoreModal({ isOpen: false, businessId: null, businessName: '' });
    setErrorMessage('');
  };

  const handleRestore = async () => {
    setActionLoading(true);
    setErrorMessage('');
    
    try {
      await adminApi.patch(
        `/admin/businesses/${restoreModal.businessId}/archive?archived=false`,
        {}
      );
      toast.success('Business restored successfully!');
      closeRestoreModal();
      fetchArchivedBusinesses();
    } catch (err) {
      const d = err.response?.data?.detail;
      setErrorMessage(typeof d === 'string' ? d : d?.message || 'Failed to restore business');
    } finally {
      setActionLoading(false);
    }
  };

  const openDeleteModal = (business) => {
    setDeleteModal({ isOpen: true, businessId: business.id, businessName: business.name });
    setErrorMessage('');
  };

  const closeDeleteModal = () => {
    setDeleteModal({ isOpen: false, businessId: null, businessName: '' });
    setErrorMessage('');
  };

  const handleDelete = async () => {
    setActionLoading(true);
    setErrorMessage('');
    
    try {
      await adminApi.delete(`/admin/businesses/${deleteModal.businessId}`);
      toast.success('Business permanently deleted');
      closeDeleteModal();
      fetchArchivedBusinesses();
    } catch (err) {
      const d = err.response?.data?.detail;
      setErrorMessage(typeof d === 'string' ? d : d?.message || 'Failed to delete business');
    } finally {
      setActionLoading(false);
    }
  };

  const filteredBusinesses = businesses.filter(b =>
    b.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  // Loading state
  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-business border-t-transparent"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      {/* V5 Page Header */}
      <div className="mb-8">
        <p className="font-mono text-business text-sm mb-1">// archived_businesses</p>
        <h1 className="font-display text-3xl text-navy">Archived Businesses</h1>
        <p className="text-navy/50 mt-1">
          Restore or permanently delete archived businesses • {businesses.length} archived
        </p>
      </div>

      {/* V5 Search Card */}
      <div className="bg-white rounded-2xl p-5 mb-6 border border-navy/5 shadow-sm" style={{ borderLeft: '4px solid #6F5BEB' }}>
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="flex-1 relative">
            <Search size={18} className="absolute left-4 top-1/2 transform -translate-y-1/2 text-navy/30" />
            <Input
              placeholder="Search by business name..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-11 h-12 border-2 border-navy/10 focus:border-business rounded-xl"
            />
          </div>
          <Button 
            onClick={fetchArchivedBusinesses}
            className="h-12 px-6"
            style={{ background: 'linear-gradient(135deg, #6F5BEB, #5B4BC9)', color: 'white' }}
          >
            <Search size={18} className="mr-2" />
            Search
          </Button>
        </div>
      </div>

      {/* V5 Results Card */}
      <div className="bg-white rounded-2xl border border-navy/5 shadow-sm overflow-hidden">
        {filteredBusinesses.length === 0 ? (
          <div className="p-12 text-center">
            <div className="w-20 h-20 bg-navy/5 rounded-full flex items-center justify-center mx-auto mb-6">
              <Archive className="w-10 h-10 text-navy/20" />
            </div>
            <h3 className="font-display text-xl text-navy mb-2">No Archived Businesses</h3>
            <p className="text-navy/50">
              {searchQuery ? 'No archived businesses match your search' : 'No businesses have been archived'}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-navy/5 bg-cream/50">
                  <th className="px-6 py-4 text-left text-xs font-semibold text-navy/50 uppercase tracking-wide">Business</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-navy/50 uppercase tracking-wide">Category</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-navy/50 uppercase tracking-wide">Plan</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-navy/50 uppercase tracking-wide">Archived</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-navy/50 uppercase tracking-wide">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-navy/5">
                {filteredBusinesses.map((business) => (
                  <tr key={business.id} className="hover:bg-cream/30 transition-colors">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-business to-business/70 flex items-center justify-center text-white font-semibold">
                          {(business.name?.[0] || '?').toUpperCase()}
                        </div>
                        <div>
                          <p className="font-semibold text-navy">{business.name}</p>
                          {business.offer && (
                            <p className="text-sm text-navy/50 truncate max-w-[200px]">{business.offer}</p>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-navy/70">{business.category || 'Uncategorized'}</td>
                    <td className="px-6 py-4">
                      <span className="px-2.5 py-1 bg-navy/5 text-navy/70 text-sm font-medium rounded-full">
                        {tierLabel(business.plan_tier)}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className="px-2.5 py-1 bg-amber-100 text-amber-700 text-sm font-medium rounded-full">
                        Archived
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <Button
                          onClick={() => openRestoreModal(business)}
                          size="sm"
                          className="rounded-xl"
                          style={{ background: 'linear-gradient(135deg, #22c55e, #16a34a)', color: 'white' }}
                        >
                          <RotateCcw size={14} className="mr-1.5" />
                          Restore
                        </Button>
                        <Button
                          onClick={() => openDeleteModal(business)}
                          size="sm"
                          variant="outline"
                          aria-label={`Delete ${business.name}`}
                          className="rounded-xl border-red-200 text-red-600 hover:bg-red-50"
                        >
                          <Trash2 size={14} aria-hidden="true" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Restore Modal */}
      <ConfirmModal
        isOpen={restoreModal.isOpen}
        onClose={closeRestoreModal}
        onConfirm={handleRestore}
        title="Restore Business"
        message={
          <div>
            <p className="mb-2">Restore <strong>{restoreModal.businessName}</strong> to active businesses?</p>
            <p className="text-sm text-navy/50">The business will be visible again and the owner can manage it.</p>
            {errorMessage && (
              <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-800">{errorMessage}</p>
              </div>
            )}
          </div>
        }
        confirmText="Restore"
        cancelText="Cancel"
        danger={false}
        loading={actionLoading}
      />

      {/* Delete Modal */}
      <ConfirmModal
        isOpen={deleteModal.isOpen}
        onClose={closeDeleteModal}
        onConfirm={handleDelete}
        title="Permanently Delete Business"
        message={
          <div>
            <p className="mb-2">Permanently delete <strong>{deleteModal.businessName}</strong>?</p>
            <p className="text-sm text-red-600 font-medium">This action cannot be undone! All deals and data will be lost.</p>
            {errorMessage && (
              <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-800">{errorMessage}</p>
              </div>
            )}
          </div>
        }
        confirmText="Delete Permanently"
        cancelText="Cancel"
        danger={true}
        loading={actionLoading}
      />
    </AdminLayout>
  );
};

export default ArchivedBusinesses;
